import {
	useTranslationMipManage,
	useTranslationOrganization,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { OrganizationSettingsView } from '@dealstack/frontend/app/pages/Organization/Organization.types';

/**
 * The views that name themselves. General is the organisation, labelled with its own name, and the
 * two drill-downs are titled by the page they open — so none of the three belongs here.
 */
export type OrganizationSettingsSectionView = Exclude<
	OrganizationSettingsView,
	| OrganizationSettingsView.General
	| OrganizationSettingsView.User
	| OrganizationSettingsView.FundDeals
>;

export const isOrganizationSettingsSectionView = (
	view: OrganizationSettingsView
): view is OrganizationSettingsSectionView =>
	view !== OrganizationSettingsView.General &&
	view !== OrganizationSettingsView.User &&
	view !== OrganizationSettingsView.FundDeals;

/** One name per section, shared by the sidebar and the breadcrumb so the two cannot drift. */
export const useOrganizationSettingsViewLabels = (): Record<
	OrganizationSettingsSectionView,
	string
> => {
	const tOrganization = useTranslationOrganization();
	const tMipManage = useTranslationMipManage();

	return {
		[OrganizationSettingsView.Members]: tOrganization('settings.members'),
		[OrganizationSettingsView.Authentication]: tOrganization('settings.authentication'),
		[OrganizationSettingsView.Funds]: tOrganization('settings.funds'),
		[OrganizationSettingsView.ProgrammeDefaults]: tOrganization('programme_defaults.title'),
		[OrganizationSettingsView.ProgrammeDefaultsMembers]: tOrganization(
			'programme_defaults.members.title'
		),
		[OrganizationSettingsView.ProgrammeDefaultsKycSettings]: tMipManage(
			'programme_settings.labels.kyc'
		),
		[OrganizationSettingsView.ProgrammeDefaultsInvestorData]: tMipManage(
			'programme_settings.labels.investor_data'
		),
		[OrganizationSettingsView.ProgrammeDefaultsVestingTerms]: tOrganization(
			'programme_defaults.vesting_terms'
		),
		[OrganizationSettingsView.ProgrammeDefaultsMasterDocuments]: tMipManage(
			'programme_settings.labels.documents'
		),
		[OrganizationSettingsView.ProgrammeDefaultsTaxAndCompliance]: tMipManage(
			'programme_settings.labels.tax'
		),
		[OrganizationSettingsView.ProgrammeDefaultsEmailTemplates]: tMipManage(
			'programme_settings.labels.email_templates'
		),
		[OrganizationSettingsView.ProgrammeDefaultsEmailReminders]: tMipManage(
			'programme_settings.labels.automated_emails'
		),
		[OrganizationSettingsView.DealDefaults]: tOrganization('deal_defaults.title'),
		[OrganizationSettingsView.DealDefaultsMembers]: tOrganization(
			'deal_defaults.members.title'
		),
		[OrganizationSettingsView.DealDefaultsClassifiers]: tOrganization(
			'deal_defaults.classifiers.title'
		),
		[OrganizationSettingsView.DealDefaultsEmailTemplates]: tMipManage(
			'programme_settings.labels.email_templates'
		),
	};
};
